import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/auth";
import { formatMXN, formatMXN2 } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "./MerchantOrders";
import {
    ArrowLeft, MapPin, Package, Truck, CreditCard, FileText,
    Loader2, AlertCircle, Send, CheckCircle2, XCircle, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

// Acciones disponibles según el estado actual de la orden y el método de entrega.
function actionsFor(status, deliveryMethod) {
    const isPickup = deliveryMethod === "pickup";
    switch (status) {
        case "paid":
            return isPickup
                ? [
                      { to: "delivered", label: "Marcar como entregado", tone: "primary", icon: CheckCircle2 },
                      { to: "out_of_stock", label: "Marcar sin stock", tone: "warning", icon: AlertCircle },
                  ]
                : [{ to: "out_of_stock", label: "Marcar sin stock", tone: "warning", icon: AlertCircle }];
        case "label_generated":
            return [
                { to: "shipped", label: "Marcar como enviado", tone: "primary", icon: Send },
                { to: "cancelled", label: "Cancelar pedido", tone: "danger", icon: XCircle },
            ];
        case "shipped":
            return [{ to: "delivered", label: "Marcar como entregado", tone: "primary", icon: CheckCircle2 }];
        case "out_of_stock":
            return [{ to: "cancelled", label: "Cancelar (ya reembolsado)", tone: "danger", icon: XCircle }];
        default:
            return [];
    }
}

export default function MerchantOrderDetail() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState("");
    const [generatingLabel, setGeneratingLabel] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await apiFetch.get(`/merchant/orders/${orderId}`);
            setOrder(data);
        } catch (e) {
            if (e.response?.status === 404) setNotFound(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, [orderId]);

    const changeStatus = async (newStatus) => {
        if (!window.confirm(`¿Cambiar el estado del pedido a "${newStatus}"?`)) return;
        setBusy(true);
        try {
            const { data } = await apiFetch.patch(`/merchant/orders/${orderId}`, { status: newStatus });
            setOrder(data);
            toast.success("Estado actualizado");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo actualizar");
        } finally {
            setBusy(false);
        }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        setBusy(true);
        try {
            const { data } = await apiFetch.patch(`/merchant/orders/${orderId}`, { note: note.trim() });
            setOrder(data);
            setNote("");
            toast.success("Nota agregada");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo agregar la nota");
        } finally {
            setBusy(false);
        }
    };

    const generateLabel = async () => {
        setGeneratingLabel(true);
        try {
            const { data } = await apiFetch.post(`/merchant/orders/${orderId}/generate-label`);
            setOrder(data);
            toast.success("Guía generada");
        } catch (e) {
            const detail = e.response?.data?.detail;
            if (e.response?.status === 501) {
                toast.info("Generación de guías", { description: detail || "Disponible en breve." });
            } else {
                toast.error(detail || "No se pudo generar la guía");
            }
        } finally {
            setGeneratingLabel(false);
        }
    };

    if (loading) return <div className="h-64 rounded-xl bg-zinc-100 animate-pulse" />;
    if (notFound)
        return (
            <div className="text-center py-20">
                <p className="text-sm text-zinc-500">Pedido no encontrado.</p>
                <Button asChild variant="outline" className="mt-4">
                    <Link to="/dashboard/orders">Volver a pedidos</Link>
                </Button>
            </div>
        );
    if (!order) return null;

    const a = order.shippingAddress || {};
    const sh = order.shipping || {};
    const pay = order.payment || {};
    const amounts = order.amounts || {};
    const isPickup = order.deliveryMethod === "pickup";
    const actions = actionsFor(order.status, order.deliveryMethod);
    const canGenerateLabel = order.status === "paid" && !isPickup;
    const hasGuide = !!sh.guideNumber;

    return (
        <div data-testid="merchant-order-detail" className="space-y-6 max-w-4xl">
            <Link to="/dashboard/orders" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Pedidos
            </Link>

            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 font-mono">{order.orderNumber}</h1>
                        <StatusBadge status={order.status} />
                    </div>
                    <p className="mt-1 text-xs text-zinc-500">
                        {order.createdAt ? new Date(order.createdAt).toLocaleString("es-MX") : ""}
                        {order.paidAt && ` · pagado ${new Date(order.paidAt).toLocaleDateString("es-MX")}`}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {canGenerateLabel && !hasGuide && (
                        <Button
                            onClick={generateLabel}
                            disabled={generatingLabel}
                            className="bg-primary hover:bg-primary-hover text-primary-foreground"
                            data-testid="generate-label-btn"
                        >
                            {generatingLabel ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Generando…</>) : (<><FileText className="w-4 h-4 mr-1.5" /> Generar guía</>)}
                        </Button>
                    )}
                    {actions.map((act) => {
                        const Icon = act.icon;
                        const cls =
                            act.tone === "danger"
                                ? "bg-red-600 hover:bg-red-700 text-white"
                                : act.tone === "warning"
                                ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                                : "bg-primary hover:bg-primary-hover text-primary-foreground";
                        return (
                            <Button
                                key={act.to}
                                onClick={() => changeStatus(act.to)}
                                disabled={busy}
                                variant={act.tone === "warning" ? "outline" : "default"}
                                className={cls}
                            >
                                <Icon className="w-4 h-4 mr-1.5" /> {act.label}
                            </Button>
                        );
                    })}
                </div>
            </header>

            {order.status === "out_of_stock" && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                        Este pedido se pagó pero ya no había stock de algún producto.
                        Reembolsa al cliente desde tu panel de Stripe y luego marca el pedido como cancelado.
                    </div>
                </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Items */}
                <section className="lg:col-span-2 bg-white rounded-2xl border border-zinc-200 p-5">
                    <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
                        <Package className="w-4 h-4 text-zinc-400" /> Productos
                    </h2>
                    <ul className="mt-3 divide-y divide-zinc-100">
                        {(order.items || []).map((it, i) => (
                            <li key={i} className="flex gap-3 py-3 text-sm">
                                <div className="w-14 h-14 rounded-md bg-zinc-100 overflow-hidden shrink-0">
                                    {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-zinc-900">{it.productName}</div>
                                    <div className="text-xs text-zinc-500">
                                        {it.variants ? <span>{typeof it.variants === "string" ? it.variants : JSON.stringify(it.variants)} · </span> : null}
                                        {formatMXN(it.price)} × {it.quantity}
                                    </div>
                                </div>
                                <div className="text-zinc-900 font-medium shrink-0">{formatMXN(it.lineTotal)}</div>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-3 pt-3 border-t border-zinc-100 space-y-1 text-sm">
                        <Row label="Productos" value={formatMXN2(amounts.products)} />
                        <Row label={`Envío (${sh.carrier || "—"})`} value={formatMXN2(amounts.shipping)} />
                        <Row label="Total cobrado al cliente" value={formatMXN2(amounts.total)} bold />
                    </div>
                </section>

                {/* Right column */}
                <div className="space-y-6">
                    {/* Customer */}
                    <section className="bg-white rounded-2xl border border-zinc-200 p-5">
                        <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
                            <MapPin className="w-4 h-4 text-zinc-400" /> {isPickup ? "Cliente" : "Envío a"}
                        </h2>
                        <div className="mt-2 text-sm text-zinc-700 space-y-0.5">
                            <div className="font-medium text-zinc-900">{a.firstName} {a.lastName}</div>
                            {!isPickup && (
                                <>
                                    <div>{a.street} {a.exteriorNumber}{a.interiorNumber ? ` int. ${a.interiorNumber}` : ""}</div>
                                    <div>{a.neighborhood}</div>
                                    <div>{a.city}, {a.state}, CP {a.postalCode}</div>
                                </>
                            )}
                            <div className="text-zinc-500">{a.phone}</div>
                            <div className="text-zinc-500">{a.email}</div>
                            {a.reference && <div className="text-xs text-zinc-400 pt-1">{isPickup ? "Nota" : "Ref"}: {a.reference}</div>}
                        </div>
                    </section>

                    {/* Entrega / guía */}
                    <section className="bg-white rounded-2xl border border-zinc-200 p-5">
                        <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
                            <Truck className="w-4 h-4 text-zinc-400" /> {isPickup ? "Entrega" : "Paquetería"}
                        </h2>
                        {isPickup ? (
                            <div className="mt-2 text-sm text-zinc-700 space-y-1">
                                <div className="font-medium text-zinc-900">Acordada con el cliente</div>
                                <div className="text-zinc-500">Sin envío por paquetería. Contacta al cliente para coordinar:</div>
                                <div className="text-zinc-700">{a.phone}</div>
                                <div className="text-zinc-700">{a.email}</div>
                                {a.reference && <div className="text-xs text-zinc-400 pt-1">Nota del cliente: {a.reference}</div>}
                            </div>
                        ) : (
                            <div className="mt-2 text-sm text-zinc-700 space-y-0.5">
                                <div>{sh.carrier} · {sh.service}</div>
                                {sh.estimatedDelivery && <div className="text-zinc-500">{sh.estimatedDelivery}</div>}
                                <div className="text-zinc-500">Peso: {sh.package?.weight} kg</div>
                                {hasGuide ? (
                                    <div className="mt-2 pt-2 border-t border-zinc-100 space-y-1">
                                        <div className="text-xs text-zinc-500">Guía: <span className="font-mono text-zinc-800">{sh.guideNumber}</span></div>
                                        {sh.guidePdfUrl && (
                                            <a href={sh.guidePdfUrl} target="_blank" rel="noreferrer" className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline">
                                                Descargar PDF <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                        {sh.trackingUrl && (
                                            <a href={sh.trackingUrl} target="_blank" rel="noreferrer" className="block text-xs text-zinc-500 hover:underline">
                                                Rastrear envío
                                            </a>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-xs text-zinc-400 pt-1">
                                        {order.status === "paid" ? "Genera la guía cuando estés listo para enviar." : "Aún no hay guía."}
                                    </div>
                                )}
                            </div>
                        )}
                    </section>

                    {/* Payment */}
                    <section className="bg-white rounded-2xl border border-zinc-200 p-5">
                        <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5">
                            <CreditCard className="w-4 h-4 text-zinc-400" /> Pago
                        </h2>
                        <div className="mt-2 text-sm space-y-1">
                            <Row label="Estado" value={pay.status === "paid" ? "Pagado" : pay.status === "refunded" ? "Reembolsado" : "Pendiente"} />
                            {pay.stripeFee != null && <Row label="Comisión Stripe" value={formatMXN2(pay.stripeFee)} />}
                            {pay.merchantPayoutAmount != null && <Row label="Tu transferencia" value={formatMXN2(pay.merchantPayoutAmount)} bold />}
                            <div className="text-[11px] text-zinc-400 pt-1">
                                Transferencia = productos − comisión de Stripe. El envío lo retiene Tiendify.
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* Notes */}
            <section className="bg-white rounded-2xl border border-zinc-200 p-5">
                <h2 className="text-sm font-semibold text-zinc-900">Notas internas</h2>
                <p className="text-xs text-zinc-500 mt-0.5">Solo tú las ves. El cliente no recibe estas notas.</p>
                {(order.merchantNotes || []).length > 0 && (
                    <ul className="mt-3 space-y-2">
                        {order.merchantNotes.map((n, i) => (
                            <li key={i} className="text-sm text-zinc-700 bg-zinc-50 rounded-lg px-3 py-2">
                                {n.text}
                                <span className="block text-[11px] text-zinc-400 mt-0.5">{n.at ? new Date(n.at).toLocaleString("es-MX") : ""}</span>
                            </li>
                        ))}
                    </ul>
                )}
                <div className="mt-3 flex gap-2">
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej. cliente pidió empaque de regalo…" rows={2} maxLength={1000} className="flex-1" />
                    <Button onClick={addNote} disabled={busy || !note.trim()} variant="outline" className="self-end">Agregar</Button>
                </div>
            </section>

            {/* History */}
            {(order.statusHistory || []).length > 0 && (
                <section className="bg-white rounded-2xl border border-zinc-200 p-5">
                    <h2 className="text-sm font-semibold text-zinc-900">Historial</h2>
                    <ol className="mt-3 space-y-2">
                        {order.statusHistory.map((h, i) => (
                            <li key={i} className="flex items-center gap-3 text-sm">
                                <span className="w-2 h-2 rounded-full bg-zinc-300 shrink-0" />
                                <StatusBadge status={h.status} />
                                <span className="text-xs text-zinc-400">{h.at ? new Date(h.at).toLocaleString("es-MX") : ""}</span>
                            </li>
                        ))}
                    </ol>
                </section>
            )}
        </div>
    );
}

function Row({ label, value, bold }) {
    return (
        <div className="flex items-center justify-between">
            <span className={bold ? "font-semibold text-zinc-900" : "text-zinc-500"}>{label}</span>
            <span className={bold ? "font-semibold text-zinc-900" : "text-zinc-700"}>{value}</span>
        </div>
    );
}
